"use client"

import React, { useState, useEffect, forwardRef } from "react"
import ReactDatePicker, { registerLocale } from "react-datepicker"
import { ko } from "date-fns/locale"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

// react-datepicker CSS import
import "react-datepicker/dist/react-datepicker.css"

// 한국어 로케일 등록
registerLocale('ko', ko)

interface DatePickerProps {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  disabledDates?: Date[]
  maxDate?: Date
}

// DatePicker 컴포넌트의 커스텀 input
const CustomInput = forwardRef<
  HTMLButtonElement,
  {
    value?: string
    onClick?: () => void
    placeholder?: string
    className?: string
    disabled?: boolean
  }
>(({ value, onClick, placeholder = "날짜를 선택하세요", className, disabled }, ref) => (
  <Button
    ref={ref}
    variant="outline"
    className={cn(
      "w-full justify-start text-left font-normal",
      !value && "text-muted-foreground",
      className
    )}
    onClick={onClick}
    disabled={disabled}
    type="button"
  >
    <CalendarIcon className="mr-2 h-4 w-4" />
    {value || <span>{placeholder}</span>}
  </Button>
))

CustomInput.displayName = "CustomInput"

export function DatePicker({
  date,
  onDateChange,
  placeholder = "날짜를 선택하세요",
  className,
  disabled = false,
  disabledDates = [],
  maxDate,
}: DatePickerProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(date)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setSelectedDate(date)
  }, [date])

  const handleSelect = (date: Date | null) => {
    const newDate = date || undefined
    setSelectedDate(newDate)
    onDateChange?.(newDate)
    setIsOpen(false)
  }

  const isDateDisabled = (date: Date) => {
    // 과거 날짜 및 오늘 비활성화 (내일부터 선택 가능)
    const today = new Date()
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const compareToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    
    const isPastOrToday = compareDate <= compareToday
    
    if (isPastOrToday) {
      return true
    }
    
    // maxDate 체크
    if (maxDate) {
      const compareMaxDate = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
      if (compareDate > compareMaxDate) {
        return true
      }
    }
    
    // 특정 비활성화 날짜 체크
    const isDisabledDate = disabledDates.some(
      disabledDate => {
        const compareDisabled = new Date(disabledDate.getFullYear(), disabledDate.getMonth(), disabledDate.getDate())
        return compareDisabled.getTime() === compareDate.getTime()
      }
    )
    
    return isDisabledDate
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <CustomInput
          value={selectedDate ? format(selectedDate, "yyyy/MM/dd") : ""}
          onClick={() => !disabled && setIsOpen(true)}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 border-none shadow-none bg-transparent" align="start">
        <div className="react-datepicker-custom">
          <ReactDatePicker
            selected={selectedDate}
            onChange={handleSelect}
            locale="ko"
            dateFormat="yyyy/MM/dd"
            inline
            filterDate={(date) => !isDateDisabled(date)}
            minDate={(() => {
              const tomorrow = new Date()
              tomorrow.setDate(tomorrow.getDate() + 1)
              return tomorrow
            })()}
            excludeDates={disabledDates}
            showPopperArrow={false}
            calendarClassName="custom-calendar"
            dayClassName={(date) => {
              const isToday = new Date().toDateString() === date.toDateString()
              const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString()
              const isDisabled = isDateDisabled(date)
              
              return cn(
                "custom-day",
                isToday && "today",
                isSelected && "selected",
                isDisabled && "disabled"
              )
            }}
            monthsShown={1}
            autoFocus={false}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

// CSS 스타일을 컴포넌트에 추가
const styles = `
.react-datepicker-custom .react-datepicker {
  font-family: system-ui, -apple-system, sans-serif;
  border: 1px solid hsl(var(--border));
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  background-color: hsl(var(--background));
  overflow: hidden;
  min-width: 320px;
}

.react-datepicker-custom .react-datepicker__header {
  background: linear-gradient(to bottom, hsl(var(--background)), hsl(var(--background) / 0.98));
  border-bottom: 1px solid hsl(var(--border));
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  padding: 1rem 1.25rem 0.75rem 1.25rem;
  position: relative;
}

.react-datepicker-custom .react-datepicker__current-month {
  color: hsl(var(--foreground));
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  text-align: center;
  letter-spacing: -0.025em;
}

.react-datepicker-custom .react-datepicker__day-names {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
  padding: 0 0.25rem;
}

.react-datepicker-custom .react-datepicker__day-name {
  color: hsl(var(--muted-foreground));
  font-size: 0.8rem;
  font-weight: 600;
  width: 2.5rem;
  height: 2rem;
  line-height: 2rem;
  text-align: center;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
}

.react-datepicker-custom .react-datepicker__month {
  margin: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.react-datepicker-custom .react-datepicker__week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.125rem;
}

.react-datepicker-custom .react-datepicker__day {
  color: hsl(var(--foreground));
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  width: 2.5rem;
  height: 2.5rem;
  margin: 0;
  border-radius: 0.625rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: transparent;
  border: 2px solid transparent;
}

.react-datepicker-custom .react-datepicker__day:hover:not(.react-datepicker__day--disabled):not(.react-datepicker__day--outside-month) {
  background-color: #FFF4E6;
  color: #F39800;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(243, 152, 0, 0.15);
  border-color: #F39800;
}

.react-datepicker-custom .react-datepicker__day--selected {
  background: linear-gradient(135deg, #F39800, #FF9500) !important;
  color: white !important;
  font-weight: 600;
  transform: scale(1.08);
  box-shadow: 0 8px 16px -4px rgba(243, 152, 0, 0.4);
  border-color: transparent !important;
}

.react-datepicker-custom .react-datepicker__day--selected:hover {
  background: linear-gradient(135deg, #E08700, #F39800) !important;
  transform: scale(1.1);
  box-shadow: 0 10px 20px -4px rgba(243, 152, 0, 0.5);
}

.react-datepicker-custom .react-datepicker__day--today:not(.react-datepicker__day--selected) {
  background-color: #FFF9F0;
  color: #F39800;
  font-weight: 600;
  border: 2px solid #F39800;
  position: relative;
}

.react-datepicker-custom .react-datepicker__day--today:not(.react-datepicker__day--selected)::after {
  content: '';
  position: absolute;
  bottom: 3px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background-color: #F39800;
  border-radius: 50%;
}

.react-datepicker-custom .react-datepicker__day--today:not(.react-datepicker__day--selected):hover {
  background-color: #FFF4E6;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(243, 152, 0, 0.2);
}

.react-datepicker-custom .react-datepicker__day--disabled {
  color: hsl(var(--muted-foreground)) !important;
  cursor: not-allowed !important;
  opacity: 0.4;
  background-color: hsl(var(--muted) / 0.5) !important;
  transform: none !important;
  border-color: transparent !important;
  position: relative;
  text-decoration: line-through;
}

.react-datepicker-custom .react-datepicker__day--disabled:hover {
  background-color: hsl(var(--muted) / 0.5) !important;
  transform: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
}

.react-datepicker-custom .react-datepicker__day--disabled::after {
  content: '예약불가';
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;
}

.react-datepicker-custom .react-datepicker__day--disabled:hover::after {
  opacity: 1;
}

.react-datepicker-custom .react-datepicker__day--outside-month {
  color: hsl(var(--muted-foreground));
  opacity: 0.2;
  visibility: hidden;
}

.react-datepicker-custom .react-datepicker__navigation {
  background: hsl(var(--background));
  border: 1px solid hsl(var(--border));
  cursor: pointer;
  outline: none;
  top: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1;
  position: absolute;
}

.react-datepicker-custom .react-datepicker__navigation:hover {
  background-color: #FFF4E6;
  border-color: #F39800;
  transform: scale(1.1);
}

.react-datepicker-custom .react-datepicker__navigation:active {
  transform: scale(0.95);
}

.react-datepicker-custom .react-datepicker__navigation-icon {
  position: relative;
  top: 0;
  font-size: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.react-datepicker-custom .react-datepicker__navigation-icon::before {
  border-color: hsl(var(--foreground));
  border-width: 2px 2px 0 0;
  width: 0.45rem;
  height: 0.45rem;
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
  transform-origin: center;
}

.react-datepicker-custom .react-datepicker__navigation--previous .react-datepicker__navigation-icon::before {
  transform: translate(-25%, -50%) rotate(-135deg);
}

.react-datepicker-custom .react-datepicker__navigation--next .react-datepicker__navigation-icon::before {
  transform: translate(-75%, -50%) rotate(45deg);
}

.react-datepicker-custom .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
  border-color: #F39800;
}

.react-datepicker-custom .react-datepicker__navigation--previous {
  left: 1rem;
}

.react-datepicker-custom .react-datepicker__navigation--next {
  right: 1rem;
}

/* 애니메이션 추가 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.react-datepicker-custom .react-datepicker {
  animation: fadeIn 0.2s ease-out;
}

/* 모바일 최적화 */
@media (max-width: 640px) {
  .react-datepicker-custom .react-datepicker {
    font-size: 0.875rem;
    border-radius: 0.75rem;
    min-width: 280px;
  }
  
  .react-datepicker-custom .react-datepicker__header {
    padding: 0.75rem 1rem 0.5rem 1rem;
    border-top-left-radius: 0.75rem;
    border-top-right-radius: 0.75rem;
  }
  
  .react-datepicker-custom .react-datepicker__current-month {
    font-size: 1rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }
  
  .react-datepicker-custom .react-datepicker__day {
    width: 2.25rem;
    height: 2.25rem;
    font-size: 0.85rem;
  }
  
  .react-datepicker-custom .react-datepicker__day-name {
    width: 2.25rem;
    height: 1.75rem;
    line-height: 1.75rem;
    font-size: 0.75rem;
  }
  
  .react-datepicker-custom .react-datepicker__month {
    margin: 0.75rem;
  }
  
  .react-datepicker-custom .react-datepicker__navigation {
    top: 0.75rem;
    width: 1.75rem;
    height: 1.75rem;
  }
  
  .react-datepicker-custom .react-datepicker__navigation--previous {
    left: 0.75rem;
  }
  
  .react-datepicker-custom .react-datepicker__navigation--next {
    right: 0.75rem;
  }
  
  .react-datepicker-custom .react-datepicker__navigation-icon::before {
    width: 0.4rem;
    height: 0.4rem;
    border-width: 1.5px 1.5px 0 0;
  }
}
`

// 스타일 주입
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('react-datepicker-custom-styles')
  if (!styleElement) {
    const style = document.createElement('style')
    style.id = 'react-datepicker-custom-styles'
    style.textContent = styles
    document.head.appendChild(style)
  }
}